import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);
const USER_CACHE_KEY = "flowscribe.user.cache";
// Heartbeat: every 5 minutes when the page is visible we hit /auth/refresh
// to roll the cookie forward. Keeps the session alive forever while the tab
// is open.
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

function readCached() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCached(user) {
  if (typeof window === "undefined") return;
  try {
    if (user) {
      window.localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    } else {
      window.localStorage.removeItem(USER_CACHE_KEY);
    }
  } catch (err) {
    console.warn("[auth] cache write failed", err);
  }
}

export function AuthProvider({ children }) {
  // Hydrate from localStorage so the UI renders instantly — no full-page
  // "Checking session…" freeze even on a flaky connection.
  const initial = readCached();
  const [user, setUserState] = useState(initial);
  // Only block the UI on first load if we have no cached user.
  const [loading, setLoading] = useState(!initial);
  const refreshTimerRef = useRef(null);
  const inFlightCheckRef = useRef(false);

  const setUser = useCallback((u) => {
    setUserState(u);
    writeCached(u);
  }, []);

  // Background validation: call /me silently, never block the UI on it.
  const checkAuth = useCallback(async ({ silent = true } = {}) => {
    if (inFlightCheckRef.current) return;
    inFlightCheckRef.current = true;
    // 5-second hard ceiling. If /me hangs (cold backend, flaky network), we
    // still drop the loading spinner so the user can either click into the
    // app (with cached user) or land on /login.
    const safety = window.setTimeout(() => setLoading(false), 5000);
    try {
      const { data } = await api.get("/auth/me", { timeout: 8000 });
      setUser(data);
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
      } else if (!silent) {
        console.warn("[auth] /me check failed", err);
      }
      // For network errors, KEEP the cached user. Don't force a logout.
    } finally {
      window.clearTimeout(safety);
      setLoading(false);
      inFlightCheckRef.current = false;
    }
  }, [setUser]);

  // Heartbeat: roll the cookie forward while the page is open.
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.post("/auth/refresh");
      setUser(data);
    } catch (err) {
      // 401 means the cookie is dead → drop the cached user.
      if (err.response?.status === 401) setUser(null);
    }
  }, [setUser]);

  useEffect(() => {
    // Initial verification — but never blocks the UI past the cached state.
    checkAuth({ silent: !!initial });
    // Heartbeat refresh
    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      refresh();
    };
    refreshTimerRef.current = window.setInterval(tick, REFRESH_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      if (refreshTimerRef.current) window.clearInterval(refreshTimerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, refresh: checkAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
